import { FormControl, FormHelperText, Stack, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { FileUploadIcon } from '@/shared/assets/icons/file-upload-icon';
import { AcceptedFiles } from '@/shared/components/ui/form/components/file-dropzone/components/accepted-files/accepted-files';
import { TDropzoneInputProps } from '@/shared/components/ui/form/components/file-dropzone/components/dropzone-input/dropzone-input.types';
import { FileUploaded } from '@/shared/components/ui/form/components/file-dropzone/components/file-uploaded/file-uploaded';
import { useDropzoneStyles } from '@/shared/components/ui/form/components/file-dropzone/hooks/use-dropzone-styles/use-dropzone-styles';
import {
  FILE_TYPE_NOT_SUPPORTED_ERROR_CODE,
  useFileValidator,
} from '@/shared/components/ui/form/components/file-dropzone/hooks/use-file-validator/use-file-validator';
import { FormErrorMessage } from '@/shared/components/ui/form/components/form-error-message/form-error-message';
import { FormLabel } from '@/shared/components/ui/form/components/form-label/form-label';

import { DropzoneContainer } from '../../file-dropzone.styled';

export const DropzoneInput = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  accept,
  maxFiles = 1,
  maxSize,
  onFilesSelected,
  disabled: _disabled,
  required,
  acceptedFileTypes = [],
  onRemove,
  errorMessage,
  renderFileOptions,
}: TDropzoneInputProps) => {
  const { t } = useTranslation();
  const validateFileExtension = useFileValidator(acceptedFileTypes);

  const disabled =
    _disabled || (Array.isArray(value) && value.length >= maxFiles);

  const getFiles = (acceptedFiles: File[]) => {
    if (maxFiles === 1) {
      return acceptedFiles[0];
    }

    if (Array.isArray(value)) {
      return [
        ...acceptedFiles,
        ...value.slice(0, maxFiles - acceptedFiles.length),
      ];
    }

    return acceptedFiles;
  };

  const handleDrop = (acceptedFiles: File[]) => {
    onChange(getFiles(acceptedFiles));
    onFilesSelected?.(acceptedFiles);
  };

  const handleRemove = (fileToRemove: File) => {
    if (Array.isArray(value) && value.length > 1) {
      onChange(value.filter((file) => file !== fileToRemove));
    } else {
      onChange(undefined);
    }
    onRemove?.();
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone({
    validator: validateFileExtension,
    onDrop: handleDrop,
    accept,
    maxFiles,
    maxSize,
    disabled,
  });

  const validationErrors = fileRejections.map((rejection) =>
    rejection.errors
      .filter((error) => error.code === FILE_TYPE_NOT_SUPPORTED_ERROR_CODE)
      .map((error) => error.message),
  );

  const hasError = error || validationErrors.length > 0;
  const iconVariant = hasError ? 'error' : 'primary';

  const { dropzoneStyle, disabledStyle } = useDropzoneStyles({
    isDragActive,
    isDragAccept,
    isDragReject,
    hasError,
  });

  const dropzoneText = isDragActive
    ? t('ui:form.drop_files_here')
    : t('ui:form.drag_and_drop');

  const renderDropzoneContent = () => {
    if (value && !Array.isArray(value)) {
      return (
        <FileUploaded
          disabled={_disabled}
          value={value}
          onRemove={handleRemove}
          renderFileOptions={renderFileOptions}
        />
      );
    }

    return (
      <>
        <DropzoneContainer
          {...getRootProps({
            style: disabled ? disabledStyle : dropzoneStyle,
            disabled,
          })}
          aria-disabled={disabled}
        >
          <input {...getInputProps()} disabled={disabled} />
          <Stack alignItems="center" gap="1rem">
            <FileUploadIcon variant={iconVariant} />
            <Typography
              variant="p8"
              color={disabled ? 'text.disabled' : 'textSecondary'}
            >
              {dropzoneText}
            </Typography>
            <AcceptedFiles acceptedFileTypes={acceptedFileTypes} />
          </Stack>
        </DropzoneContainer>
        {validationErrors.map((error, index) => (
          <Typography
            key={index}
            marginTop=".5rem"
            variant="p8"
            color="status.red.text"
          >
            {error}
          </Typography>
        ))}
        {Array.isArray(value) && value.length > 0 && (
          <FileUploaded
            disabled={_disabled}
            value={value}
            onRemove={handleRemove}
            renderFileOptions={renderFileOptions}
          />
        )}
      </>
    );
  };

  return (
    <FormControl disabled={disabled} fullWidth error={error}>
      <FormLabel label={label} required={required} />
      {renderDropzoneContent()}
      {placeholder && <FormHelperText>{placeholder}</FormHelperText>}
      {error && <FormErrorMessage errorMessage={errorMessage} />}
    </FormControl>
  );
};
